import React, { useState } from 'react';
import {
  IAuditoriaContasPagarReceberResultado,
  IItemContasPagarReceber,
} from '../interfaces/IAuditoriaContasPagarReceber';
import { formatCurrency, formatDate } from '../utils/formatters';

interface Props {
  resultado: IAuditoriaContasPagarReceberResultado;
}


const getIconeStatus = (status: string) => {
  switch (status) {
    case 'ERRO':
      return 'bi-exclamation-circle-fill';
    case 'ALERTA':
      return 'bi-exclamation-triangle-fill';
    case 'AVISO':
      return 'bi-info-circle-fill';
    default:
      return 'bi-check-circle-fill';
  }
};

// COR-288: drill-down a nível de lançamento — todo ERRO precisa mostrar não só o saldo agregado,
// mas os lançamentos reais que compõem o saldo com natureza invertida.
const LIMITE_LANCAMENTOS_VISIVEIS = 10;

const BlocoLancamentosRelacionados: React.FC<{ item: IItemContasPagarReceber }> = ({ item }) => {
  const [mostrarTodos, setMostrarTodos] = useState(false);
  const lancamentos = item.lancamentosRelacionados || [];

  if (lancamentos.length === 0) return null;

  const visiveis = mostrarTodos ? lancamentos : lancamentos.slice(0, LIMITE_LANCAMENTOS_VISIVEIS);

  return (
    <div className='mt-2'>
      <strong>Lançamentos Relacionados ({lancamentos.length}):</strong>
      <div className='table-responsive mt-1'>
        <table className='auditoria-table' style={{ fontSize: 12 }}>
          <thead>
            <tr>
              <th>Data</th>
              <th>Nº Lançamento</th>
              <th>Histórico</th>
              <th className='text-end'>Débito</th>
              <th className='text-end'>Crédito</th>
            </tr>
          </thead>
          <tbody>
            {visiveis.map((lanc, idx) => (
              <tr key={`${lanc.numeroLancamento}-${lanc.data}-${idx}`}>
                <td>{formatDate(lanc.data)}</td>
                <td><code>{lanc.numeroLancamento || '-'}</code></td>
                <td>{lanc.historico}</td>
                <td className='text-end'>{lanc.valorDebito > 0 ? formatCurrency(lanc.valorDebito) : '-'}</td>
                <td className='text-end'>{lanc.valorCredito > 0 ? formatCurrency(lanc.valorCredito) : '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {lancamentos.length > LIMITE_LANCAMENTOS_VISIVEIS && !mostrarTodos && (
        <button
          type='button'
          className='btn btn-link btn-sm p-0 mt-1'
          style={{ fontSize: 12 }}
          onClick={() => setMostrarTodos(true)}
        >
          + {lancamentos.length - LIMITE_LANCAMENTOS_VISIVEIS} lançamento(s) adicional(is)
        </button>
      )}
    </div>
  );
};

const AuditoriaContasPagarReceberDetalhe: React.FC<Props> = ({ resultado }) => {
  const [itemExpandido, setItemExpandido] = useState<string | null>(null);
  const [fundamentacaoAberta, setFundamentacaoAberta] = useState(false);

  const contasComNaturezaInvertida = resultado.contasComNaturezaInvertida || [];
  const contasConciliadas = resultado.contasConciliadas || [];

  const alternarExpandir = (chave: string) => {
    setItemExpandido(itemExpandido === chave ? null : chave);
  };

  return (
    <>
      <div className={`auditoria-alert status-${resultado.status}`}>
        <i className={`bi ${getIconeStatus(resultado.status)} auditoria-alert-icon`} />
        <div>
          <strong>Auditoria de Contas a Pagar e a Receber — {resultado.status}</strong>
          <div>{resultado.mensagemDirecionamento}</div>
        </div>
      </div>

      <div className='auditoria-kpis'>
        <div className={`auditoria-kpi ${resultado.impactoFiscalTotal > 0 ? 'auditoria-kpi-critico' : 'auditoria-kpi-disabled'}`}>
          <div className='auditoria-kpi-label'>Impacto Fiscal Estimado</div>
          <div className='auditoria-kpi-value'>{formatCurrency(resultado.impactoFiscalTotal)}</div>
          {resultado.impactoFiscalTotal === 0 ? (
            <span className='auditoria-kpi-selo'>Zero Desperdício</span>
          ) : (
            <div className='auditoria-kpi-help'>Multa ECD potencial — 5% limitada a 1% da receita bruta do período (Art. 12, II, Lei 8.218/1991).</div>
          )}
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Erros / Alertas / Avisos</div>
          <div className='auditoria-kpi-value'>
            <span className='auditoria-contador auditoria-contador-erro'>{resultado.totalErros}</span>{' '}
            <span className='auditoria-contador auditoria-contador-alerta'>{resultado.totalAlertas}</span>{' '}
            <span className='auditoria-contador auditoria-contador-aviso'>{resultado.totalAvisos}</span>
          </div>
          <div className='auditoria-kpi-help'>Contas de Fornecedores/Clientes com natureza de saldo invertida.</div>
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Fundamentação</div>
          <div className='auditoria-kpi-value'>
            <button
              type='button'
              className='btn btn-sm btn-light-primary'
              onClick={() => setFundamentacaoAberta(!fundamentacaoAberta)}
            >
              <i className='bi bi-journal-check me-1' />
              {fundamentacaoAberta ? 'Ocultar fundamentação' : 'Ver fundamentação legal'}
            </button>
          </div>
          <div className='auditoria-kpi-help'>Base legal aplicada à auditoria de contas a pagar e a receber.</div>
        </div>
      </div>

      {fundamentacaoAberta && (
        <div className='auditoria-card'>
          <div><strong>Natureza Invertida:</strong> {resultado.fundamentacaoNaturezaInvertida}</div>
        </div>
      )}

      {/* Contas com natureza invertida */}
      <div className='auditoria-card'>
        <h3>Contas de Fornecedores/Clientes com Natureza Invertida</h3>
        <p className='text-muted'>
          Contas de Fornecedores (Passivo) que fecharam com saldo devedor, ou contas de Clientes (Ativo) que
          fecharam com saldo credor — natureza trocada em relação ao esperado para o grupo contábil.
        </p>

        {contasComNaturezaInvertida.length === 0 && (
          <div className='text-muted mt-4'>Nenhuma conta com natureza invertida identificada no período.</div>
        )}

        {contasComNaturezaInvertida.length > 0 && (
          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>Tipo</th>
                  <th>Natureza Esperada</th>
                  <th>Natureza Encontrada</th>
                  <th className='text-end'>Saldo Final</th>
                  <th className='text-end'>Multa ECD Potencial</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {contasComNaturezaInvertida.map((item: IItemContasPagarReceber, idx) => {
                  const chave = `cpr-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          {item.contaReferencial && <div className='text-muted' style={{ fontSize: 11 }}>{item.contaReferencial}</div>}
                        </td>
                        <td>{item.tipoConta === 'FORNECEDOR' ? 'Fornecedor (Passivo)' : 'Cliente (Ativo)'}</td>
                        <td>{item.naturezaEsperada}</td>
                        <td>{item.naturezaEncontrada}</td>
                        <td className='text-end'>{formatCurrency(item.saldoFinal)}</td>
                        <td className='text-end'>{formatCurrency(item.multaEcd)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={7} style={{ background: '#fafbfc' }}>
                            <div className='py-2 px-3'>
                              <div><strong>Diagnóstico:</strong> {item.mensagemDiagnostico}</div>
                              <div><strong>Memória de Cálculo:</strong> {item.memoriaCalculo}</div>
                              <BlocoLancamentosRelacionados item={item} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Contas conciliadas — visibilidade de que a auditoria rodou */}
      {contasConciliadas.length > 0 && (
        <div className='auditoria-card'>
          <h3>
            <i className='bi bi-check-circle-fill text-success me-2' style={{ fontSize: 18 }} />
            Contas de Fornecedores/Clientes Verificadas e Conciliadas
          </h3>
          <p className='text-muted'>
            Contas auditadas que fecharam o período com a natureza de saldo correta (ou sem saldo).
          </p>

          <div className='table-responsive mt-4'>
            <table className='auditoria-table'>
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>Tipo</th>
                  <th className='text-end'>Saldo Final</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {contasConciliadas.map((item: IItemContasPagarReceber, idx) => {
                  const chave = `cprc-${item.empresaId}-${item.contaContabil}-${idx}`;
                  const expandido = itemExpandido === chave;

                  return (
                    <React.Fragment key={chave}>
                      <tr className='auditoria-row-tributo' onClick={() => alternarExpandir(chave)}>
                        <td>
                          <strong>{item.descricaoConta || item.contaContabil}</strong>
                          {item.contaReferencial && <div className='text-muted' style={{ fontSize: 11 }}>{item.contaReferencial}</div>}
                        </td>
                        <td>{item.tipoConta === 'FORNECEDOR' ? 'Fornecedor (Passivo)' : 'Cliente (Ativo)'}</td>
                        <td className='text-end'>{formatCurrency(item.saldoFinal)}</td>
                        <td>
                          <span className={`auditoria-status status-${item.status}`}>{item.status}</span>
                          <i className={`bi ${expandido ? 'bi-chevron-up' : 'bi-chevron-down'} ms-2 text-muted`} />
                        </td>
                      </tr>

                      {expandido && (
                        <tr>
                          <td colSpan={4} style={{ background: '#f0fdf4' }}>
                            <div className='py-2 px-3'>
                              <i className='bi bi-check-circle-fill text-success me-1' />
                              <strong>Diagnóstico:</strong> {item.mensagemDiagnostico}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
};

export default AuditoriaContasPagarReceberDetalhe;
